import * as XLSX from 'xlsx';
import { fetchIncotermTimeline } from '../../../Components/QuantityTable/utils/quantityDeliveryUtils';
import {
  getIncotermBasesFromParcel,
  fetchQuantityInformationTargets,
  buildLpgIncotermTableModel,
  formatParcelSizes,
  fetchAllCargoRatioSelections,
  fetchCargoRatioOptions,
  getConfirmedRatiosFromSelections,
  fetchIncotermAlphaMap,
} from '../component/HpsParcel/lpg/utils';

const PARCEL_EXPORT_COLUMNS = [
  { header: 'ID', key: 'id' },
  { header: 'Incoterm Location ID', key: 'incotermLocationId' },
  { header: 'Delivery Unit', key: 'deliveryUnit' },
  { header: 'Parcel Size', key: 'parcelSize' },
  { header: 'Incoterm', key: 'incoterm' },
  { header: 'Route', key: 'route' },
  { header: 'Loading Port', key: 'loadingPort' },
  { header: 'Discharge Qty 1', key: 'dischargeQty1' },
  { header: 'Port 1', key: 'port1' },
  { header: 'ALD/ADD 1', key: 'aldAdd1' },
  { header: 'Discharge Qty 2', key: 'dischargeQty2' },
  { header: 'Port 2', key: 'port2' },
  { header: 'ALD/ADD 2', key: 'aldAdd2' },
  { header: 'Discharge Qty 3', key: 'dischargeQty3' },
  { header: 'Port 3', key: 'port3' },
  { header: 'ALD/ADD 3', key: 'aldAdd3' },
  { header: 'Alpha Dyed', key: 'alphaDyed' },
  { header: 'Alpha Undyed', key: 'alphaUndyed' },
];

// Nilai alpha HPS datang dari API sebagai null bila belum pernah diisi; tampilkan '-'
// supaya konsisten dengan kolom lain dan supaya sel kosong tidak terbaca sebagai 0.
const alphaValue = value =>
  value === null || value === undefined || value === '' ? '-' : value;

const isFob = row =>
  String(row?.incoterm ?? '')
    .trim()
    .toUpperCase() === 'FOB';

export const buildParcelExportRows = (parcels = []) => {
  if (!parcels?.length) return [];
  return parcels.flatMap(parcel => {
    const deliveryUnit = parcel.deliveryUnit || '-';
    const listData = parcel.listData || [];

    return listData.flatMap(item => {
      if (isFob(item)) {
        const fobLocations = item.incotermFOBLocations || [];
        if (!fobLocations.length) {
          return [
            {
              id: item.id || '-',
              incotermLocationId: '-',
              deliveryUnit,
              parcelSize: item.deliveryBasis ?? '-',
              incoterm: item.incoterm || '-',
              route: item.route || '-',
              loadingPort: '-',
              dischargeQty1: '-',
              port1: '-',
              aldAdd1: '-',
              dischargeQty2: '-',
              port2: '-',
              aldAdd2: '-',
              dischargeQty3: '-',
              port3: '-',
              aldAdd3: '-',
              alphaDyed: alphaValue(item.alphaDyedHps),
              alphaUndyed: alphaValue(item.alphaUndyedHps),
            },
          ];
        }
        return fobLocations.map(loc => ({
          id: item.id || '-',
          incotermLocationId: loc.id || '-',
          deliveryUnit,
          parcelSize: loc.deliveryBasis ?? item.deliveryBasis ?? '-',
          incoterm: item.incoterm || '-',
          route: item.route || '-',
          loadingPort: loc.name || '-',
          dischargeQty1: '-',
          port1: '-',
          aldAdd1: '-',
          dischargeQty2: '-',
          port2: '-',
          aldAdd2: '-',
          dischargeQty3: '-',
          port3: '-',
          aldAdd3: '-',
          alphaDyed: alphaValue(loc.alphaDyedHps),
          alphaUndyed: alphaValue(loc.alphaUndyedHps),
        }));
      }

      return [
        {
          id: item.id || '-',
          incotermLocationId: ' ',
          deliveryUnit,
          parcelSize: item.deliveryBasis ?? '-',
          incoterm: item.incoterm || '-',
          route: '-',
          loadingPort: '-',
          dischargeQty1: item.parcelSize ?? '-',
          port1: item.port1 || '-',
          aldAdd1: item.addAld || '-',
          dischargeQty2: item.parcelSize2 ?? '-',
          port2: item.port2 || '-',
          aldAdd2: item.addAld2 || '-',
          dischargeQty3: item.parcelSize3 ?? '-',
          port3: item.port3 || '-',
          aldAdd3: item.addAld3 || '-',
          alphaDyed: alphaValue(item.alphaDyedHps),
          alphaUndyed: alphaValue(item.alphaUndyedHps),
        },
      ];
    });
  });
};

const pad = n => String(n).padStart(2, '0');

export const downloadParcelExcel = (parcels, procurementNumber) => {
  const rows = buildParcelExportRows(parcels);
  if (!rows.length) {
    return false;
  }

  const now = new Date();
  const dateStr = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const filename = `Parcel_${procurementNumber || 'data'}_${dateStr}.xlsx`;

  const headers = PARCEL_EXPORT_COLUMNS.map(col => col.header);
  const data = rows.map(row =>
    PARCEL_EXPORT_COLUMNS.map(col => row[col.key] ?? '-')
  );

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);

  const colWidths = PARCEL_EXPORT_COLUMNS.map(col => {
    const maxLen = Math.max(
      col.header.length,
      ...rows.map(row => String(row[col.key] ?? '-').length)
    );
    return { wch: Math.min(maxLen + 2, 40) };
  });
  worksheet['!cols'] = colWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Parcel');
  XLSX.writeFile(workbook, filename);

  return true;
};

const LPG_EXPORT_COLUMNS = [
  { header: 'id', key: 'cargoRatioId' },
  { header: 'quantityIncotermFK', key: 'quantityIncotermFK' },
  { header: 'quantityInformationFK', key: 'quantityInformationFK' },
  { header: 'Delivery Unit', key: 'deliveryUnit' },
  { header: 'ALD/ADD', key: 'aldAdd' },
  { header: 'Incoterm', key: 'incoterm' },
  { header: 'Port 1', key: 'port1' },
  { header: 'Port 2', key: 'port2' },
  { header: 'Port 3', key: 'port3' },
  { header: 'Cargo Ratio', key: 'cargoRatioLabel' },
  { header: 'Parcel Size', key: 'parcelSize' },
  { header: 'Alpha', key: 'alpha' },
];

const enrichParcelWithTimeline = async (parcel, token) => {
  try {
    const { rawItems, timelineItems } = await fetchIncotermTimeline(
      parcel.id,
      token
    );
    return {
      ...parcel,
      incotermTimelineItems: timelineItems,
      incotermRawItems: rawItems,
    };
  } catch {
    return { ...parcel, incotermTimelineItems: [], incotermRawItems: [] };
  }
};

export const downloadParcelExcelLpg = async (
  parcels,
  procurementId,
  procurementNumber,
  token
) => {
  if (!parcels?.length) return false;

  const enrichedParcels = await Promise.all(
    parcels.map(parcel => enrichParcelWithTimeline(parcel, token))
  );

  const allRows = [];

  for (const parcel of enrichedParcels) {
    const incotermBases = getIncotermBasesFromParcel(parcel);

    if (!incotermBases.length) continue;

    const quantityTargets = await fetchQuantityInformationTargets(
      procurementId,
      parcel.id
    );

    const parcelSizes = formatParcelSizes(quantityTargets, 'MT');

    if (!parcelSizes.length) continue;

    const quantityFks = quantityTargets.map(t => t.quantityInformationFk);
    const selections = await fetchAllCargoRatioSelections(quantityFks);
    const ratioOptions = await fetchCargoRatioOptions();
    const confirmedRatios = getConfirmedRatiosFromSelections(
      selections,
      ratioOptions
    );

    const incotermAlphaMap = await fetchIncotermAlphaMap(parcel.id);

    const { rows } = buildLpgIncotermTableModel({
      incotermBases,
      parcelSizes,
      quantityTargets,
      selections,
      confirmedRatios,
      incotermAlphaMap,
    });

    const normalizeLabel = label => (label || '').replace(/\s+/g, '');

    const extractPort = loc => {
      if (!loc) return '';
      if (typeof loc === 'string') return loc;
      return (
        loc?.location?.name ??
        loc?.location?.Name ??
        loc?.Location?.name ??
        loc?.Location?.Name ??
        loc?.name ??
        loc?.Name ??
        ''
      );
    };

    const cargoRatioLookup = new Map();
    const rawItems = parcel.incotermRawItems || [];

    for (const item of rawItems) {
      const ports = [
        extractPort(item?.incotermLocation),
        extractPort(item?.incotermLocation2),
        extractPort(item?.incotermLocation3),
      ].filter(Boolean);

      const effectivePorts = ports.length ? ports : [''];

      for (const cr of item?.cargoRatios || []) {
        for (const port of effectivePorts) {
          const key = `${port}__${normalizeLabel(cr.cargoRatioLabel)}__${cr.quantityInformationFK}`;
          cargoRatioLookup.set(key, {
            id: cr.id,
            quantityIncotermFK: cr.quantityIncotermFK,
            quantityInformationFK: cr.quantityInformationFK,
          });
        }
      }
    }

    const qFkToParcelSize = new Map();
    quantityTargets.forEach((t, idx) => {
      qFkToParcelSize.set(t.quantityInformationFk, parcelSizes[idx]);
    });

    rows.forEach(row => {
      let match = null;
      const qFk = [...qFkToParcelSize.entries()].find(
        ([, ps]) => ps === row.parcelSize
      )?.[0];

      if (qFk) {
        const lookupKey = `${row.port1}__${normalizeLabel(row.cargoRatioLabel)}__${qFk}`;
        match = cargoRatioLookup.get(lookupKey);
      }

      allRows.push({
        cargoRatioId: match?.id || '-',
        quantityIncotermFK: match?.quantityIncotermFK || '-',
        quantityInformationFK: match?.quantityInformationFK || '-',
        deliveryUnit: parcel.deliveryUnit || '-',
        aldAdd: row.port1AldAdd || row.aldAdd || '-',
        incoterm: row.incotermRoute || row.incoterm || '-',
        port1: row.port1 || row.port || '-',
        port2: row.port2 || '-',
        port3: row.port3 || '-',
        cargoRatioLabel: row.cargoRatioLabel || '-',
        parcelSize: row.parcelSize || '-',
        alpha: row.alpha ?? 0,
      });
    });
  }

  if (!allRows.length) return false;

  const now = new Date();
  const dateStr = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const filename = `Parcel_LPG_${procurementNumber || 'data'}_${dateStr}.xlsx`;

  const headers = LPG_EXPORT_COLUMNS.map(col => col.header);
  const data = allRows.map(row =>
    LPG_EXPORT_COLUMNS.map(col => row[col.key] ?? '-')
  );

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);

  const colWidths = LPG_EXPORT_COLUMNS.map(col => {
    const maxLen = Math.max(
      col.header.length,
      ...allRows.map(row => String(row[col.key] ?? '-').length)
    );
    return { wch: Math.min(maxLen + 2, 40) };
  });
  worksheet['!cols'] = colWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Parcel LPG');
  XLSX.writeFile(workbook, filename);

  return true;
};
